import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { CompanyInfo } from '../../core/auth/auth.model';
import { User } from '../../core/models/user.model';
import { LoaderService } from '../../core/services/loader.service';
import { NotificationService } from '../../core/services/notification.service';
import { NotificationActionService } from '../../core/services/notification-action.service';
import { ToastService } from '../../core/services/toast.service';
import { InAppNotification } from '../../core/models/notification.model';

@Component({
  standalone: false,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() menuToggle = new EventEmitter<void>();

  currentUser:     User        | null = null;
  selectedCompany: CompanyInfo | null = null;
  userMenuOpen = false;

  // ── Notifications ───────────────────────────────────────────────────────
  /** Whether the notification dropdown panel is open. */
  notificationsOpen = false;
  /** Unread count driving the bell badge (kept live by NotificationService). */
  unreadCount = 0;
  /** Notifications currently shown in the dropdown (kept live by NotificationService). */
  notifications: InAppNotification[] = [];
  /** True while the initial notification load is in flight. */
  loadingNotifications = false;

  /**
   * Company awaiting switch confirmation. When set, the confirmation dialog
   * ("Do you want to switch the company?") is shown. The switch is only
   * applied when the user confirms; on cancel it is discarded and the current
   * selection is kept.
   */
  pendingCompany: CompanyInfo | null = null;

  /** Two-way model bound to the p-select. Mirrors the active company code. */
  selectModel = '';

  // ── Search popup ──────────────────────────────────────────────────────────
  /** Whether the search popup modal is open. */
  searchOpen = false;
  /** Current search term entered in the popup. */
  searchTerm = '';

  private readonly destroy$ = new Subject<void>();
  private loaderTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly authService:         AuthService,
    private readonly router:              Router,
    private readonly loaderService:       LoaderService,
    private readonly notificationService: NotificationService,
    private readonly notificationAction:  NotificationActionService,
    private readonly toastService:        ToastService,
  ) {}

  ngOnInit(): void {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        // Login: load notifications once, then connect to the real-time hub.
        // This also fires on every token refresh; initialize() is idempotent per user.
        // Logout (user === null): close the connection and clear all notification state.
        if (user) {
          this.notificationService.initialize(user.username);
        } else {
          this.notificationService.disconnect();
          this.notificationsOpen = false;
        }
      });

    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => { this.unreadCount = count; });

    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => { this.notifications = list; });

    this.notificationService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => { this.loadingNotifications = loading; });

    // A notification arrived in real time: badge and list are already updated by the
    // service; surface it immediately as a toast.
    this.notificationService.incoming$
      .pipe(takeUntil(this.destroy$))
      .subscribe(n => this.toastService.info(n.message, n.title));

    this.authService.selectedCompany$
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        this.selectedCompany = company;
        // Keep the p-select model in sync with the active company so it
        // reflects external changes (login, or clearing after a delete).
        this.selectModel = company?.compCode ?? '';
      });
  }

  ngOnDestroy(): void {
    if (this.loaderTimer) clearTimeout(this.loaderTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Companies available in the header selector.
   * Sourced exclusively from the login response (User.companies).
   * Only companies the authenticated user has rights to are shown —
   * no separate API call is made.
   */
  get allCompanies(): CompanyInfo[] {
    return this.currentUser?.companies ?? [];
  }

  /** True when the user has access to more than one company. */
  get isMultiCompany(): boolean {
    return this.allCompanies.length > 1;
  }

  /**
   * Show the type-ahead search in the company selector only when the list is
   * long enough to warrant it (more than 5 companies). For 5 or fewer, the
   * plain scrollable list is easier than a search box.
   */
  get showCompanyFilter(): boolean {
    return this.allCompanies.length > 5;
  }

  /**
   * Called when the user picks a different company from the header selector.
   *
   * Instead of switching immediately, this opens the confirmation dialog
   * ("Do you want to switch the company?"). Selecting the already-active
   * company is a no-op and shows no dialog.
   */
  onCompanyChange(compCode: string): void {
    const chosen = this.allCompanies.find(c => c.compCode === compCode);
    if (!chosen || chosen.compCode === this.selectedCompany?.compCode) return;

    this.pendingCompany = chosen;
  }

  /**
   * Applies the pending company switch after the user confirms.
   * Persists the full CompanyInfo via AuthService, then navigates to the
   * dashboard so no screen retains stale data from the previous company.
   */
  confirmSwitch(): void {
    const chosen = this.pendingCompany;
    this.pendingCompany = null;
    if (!chosen) return;

    this.loaderService.show();
    this.authService.setSelectedCompany(chosen);
    this.router.navigate(['/dashboard']).finally(() => {
      // Navigation resolves almost instantly, so keep the loader visible
      // for a short beat before hiding it.
      this.loaderTimer = setTimeout(() => this.loaderService.hide(), 800);
    });
  }

  /**
   * Cancels a pending company switch. The current selection is kept and the
   * p-select model is restored to the active company code.
   */
  cancelSwitch(): void {
    this.pendingCompany = null;
    this.selectModel = this.selectedCompany?.compCode ?? '';
  }

  /** Handles selection change from the PrimeNG p-select. */
  onCompanySelect(compCode: string | null, select?: { hide?: () => void; el?: { nativeElement: HTMLElement } }): void {
    // Close the panel and drop focus so no option stays highlighted/focused.
    select?.hide?.();
    const focused = document.activeElement as HTMLElement | null;
    focused?.blur?.();

    if (compCode) this.onCompanyChange(compCode);
  }

  /** Convenience: the active company code string (empty when none selected). */
  get selectedCompanyCode(): string {
    return this.selectedCompany?.compCode ?? '';
  }

  /** First two initials of the username. */
  get userInitials(): string {
    const name  = this.currentUser?.username || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase() || 'U';
  }

  toggleUserMenu(): void { this.userMenuOpen = !this.userMenuOpen; }

  // ── Search popup ──────────────────────────────────────────────────────────
  @ViewChild('searchBox') searchBox?: ElementRef<HTMLInputElement>;

  /** Open/close the search popup. Opening also closes the other menus and
      focuses the input on the next tick. */
  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) {
      this.userMenuOpen = false;
      this.notificationsOpen = false;
      setTimeout(() => this.searchBox?.nativeElement.focus(), 0);
    }
  }

  /** Close the search popup (keeps the last term so it is there on reopen). */
  closeSearch(): void {
    this.searchOpen = false;
  }

  /** Run the search. Placeholder behaviour: closes the popup. Wire this to a
      real search action/route when the search backend is available. */
  onSearchSubmit(): void {
    const term = this.searchTerm.trim();
    if (!term) return;
    // TODO: route to a results page or emit the term once search is implemented.
    this.searchOpen = false;
  }

  /** Escape closes the search popup when it is open. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.searchOpen) this.closeSearch();
  }

  onLogout(): void {
    this.userMenuOpen = false;
    this.authService.logout();
  }

  // ── Notifications ───────────────────────────────────────────────────────

  /**
   * Toggle the notification dropdown. The list and badge are kept current in real
   * time by NotificationService, so opening the panel does not call the server.
   */
  toggleNotifications(): void {
    this.notificationsOpen = !this.notificationsOpen;
    // Close the user menu if it was open — only one dropdown at a time.
    if (this.notificationsOpen) {
      this.userMenuOpen = false;
    }
  }

  /**
   * Handle a click on a notification: mark it read (if unread), then open the related
   * request. If the user is already on the target screen the detail popup opens in place
   * (no route change); otherwise we navigate to that screen, which opens the popup from
   * the requestId query param. Closes the dropdown.
   */
  onNotificationClick(n: InAppNotification): void {
    const open = () => {
      this.notificationsOpen = false;
      if (!n.actionUrl) return;

      // actionUrl carries the request id (e.g. /employees/approvals?requestId=5).
      // Open the global read-only detail dialog in place — never navigate.
      const [path, query] = n.actionUrl.split('?');
      const requestId = Number(this.parseQuery(query)?.['requestId']);
      // Maker notifications (approved/rejected of the user's own request) deep-link
      // to a "/requests" route; approver notifications ("requires your approval")
      // deep-link to "/approvals". Title the popup accordingly.
      const title = path?.includes('/requests') ? 'Request Details' : undefined;
      if (requestId > 0) this.notificationAction.openRequest(requestId, title);
    };

    if (!n.isRead) {
      // The service updates the list item and the badge once the server confirms.
      this.notificationService.markAsRead(n.notificationId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({ next: () => open(), error: () => open() });
    } else {
      open();
    }
  }

  /** Mark a single unread notification as read without navigating. */
  markRead(n: InAppNotification, event: Event): void {
    event.stopPropagation();
    if (n.isRead) return;
    this.notificationService.markAsRead(n.notificationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  /** TrackBy for the notification list. */
  trackByNotificationId(_index: number, n: InAppNotification): number {
    return n.notificationId;
  }

  // ── Notification presentation helpers (icon + colour hint per event) ────────

  /**
   * Severity key for a notification, driving the leading icon colour, the left
   * accent bar and the small type pill.
   *
   * Prefers the LIVE request status (resolved server-side) so an "Approval
   * Required" notification flips to Approved / Rejected / Returned / Cancelled
   * once the underlying request has been actioned. Falls back to the eventType
   * when no live status is available (e.g. a real-time push, or a non-approval
   * notification).
   */
  notifSeverity(n: InAppNotification): 'submitted' | 'approved' | 'rejected' | 'returned' | 'cancelled' | 'info' {
    switch ((n.requestStatus || '').toUpperCase()) {
      case 'PENDING':
      case 'SUBMITTED': return 'submitted';
      case 'APPROVED':  return 'approved';
      case 'REJECTED':  return 'rejected';
      case 'RETURNED':  return 'returned';
      case 'CANCELLED': return 'cancelled';
    }
    switch ((n.eventType || '').toUpperCase()) {
      case 'APPROVAL_SUBMITTED': return 'submitted';
      case 'APPROVAL_APPROVED':  return 'approved';
      case 'APPROVAL_REJECTED':  return 'rejected';
      case 'APPROVAL_RETURNED':  return 'returned';
      default:                   return 'info';
    }
  }

  /** PrimeIcons glyph for the leading icon badge, by severity. */
  notifIcon(n: InAppNotification): string {
    switch (this.notifSeverity(n)) {
      case 'submitted': return 'pi pi-clock';
      case 'approved':  return 'pi pi-check-circle';
      case 'rejected':  return 'pi pi-times-circle';
      case 'returned':  return 'pi pi-replay';
      case 'cancelled': return 'pi pi-ban';
      default:          return 'pi pi-bell';
    }
  }

  /** Short type label shown as a pill under the message (reflects live status). */
  notifLabel(n: InAppNotification): string {
    switch (this.notifSeverity(n)) {
      case 'submitted': return 'Approval Required';
      case 'approved':  return 'Approved';
      case 'rejected':  return 'Rejected';
      case 'returned':  return 'Returned';
      case 'cancelled': return 'Cancelled';
      default:          return 'Notification';
    }
  }

  /** Parse a URL query string into an object for Router navigation. */
  private parseQuery(query?: string): Record<string, string> | null {
    if (!query) return null;
    const params: Record<string, string> = {};
    for (const pair of query.split('&')) {
      const [k, v] = pair.split('=');
      if (k) params[decodeURIComponent(k)] = decodeURIComponent(v ?? '');
    }
    return Object.keys(params).length ? params : null;
  }
}
